export interface CollaborationType {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  description?: string;
  prerequisites?: string;
  icon?: string;
  is_digital_material?: boolean;
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Collaborator {
  id: string;
  brand_id: string;
  user_id: string;
  contact_id?: string | null;
  collaboration_type_ids?: string[];
  name: string;
  email: string;
  phone?: string;
  company?: string;
  linkedin?: string;
  job_title?: string;
  location?: string;
  website?: string;
  twitter_handle?: string;
  status: 'pending' | 'approved' | 'active' | 'completed' | 'rejected' | 'non-eligible' | 'eligible' | 'in progress';
  source_lead_id?: string;
  metric_type?: string;
  source?: string;
  medium?: string;
  channel?: string;
  insights?: string;
  remarks?: string;
  content?: Record<string, any>;
  enrichment_data?: {
    person_search?: string;
    company_search?: string;
    linkedin_search?: string;
    extraction_prompt?: string;
    llm_response?: string;
    extracted_data?: Record<string, any>;
  };
  last_enriched_at?: string;
  enrichment_source?: string;
  collaboration_opportunities?: Array<{
    type: string;
    description: string;
    confidence: number;
    next_steps: string;
  }>;
  opportunities_updated_at?: string;
  created_at: string;
  updated_at: string;
}

/** Enrollment trigger / per-email stop condition shape for collaboration campaigns */
export interface CollaborationCampaignTriggerConditions {
  collaborator_status?: string[];
  collaboration_type_ids?: string[];
}

/** Options for collaborator status in trigger/stop conditions */
export const COLLABORATOR_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'non-eligible', label: 'Non-eligible' },
  { value: 'eligible', label: 'Eligible' },
  { value: 'in progress', label: 'In progress' },
] as const;

export interface CollaborationCampaign {
  id: string;
  brand_id: string;
  user_id: string;
  collaboration_type_id?: string;
  collaboration_type_ids?: string[];
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  trigger_conditions?: CollaborationCampaignTriggerConditions | null;
  created_at: string;
  updated_at: string;
}

export interface CollaborationEmail {
  id: string;
  campaign_id: string;
  sequence_order: number;
  subject: string;
  content: string;
  delay_days: number;
  delay_hours: number;
  send_conditions?: {
    stop_if_replied?: boolean;
    stop_if_opened?: boolean;
  };
  stop_conditions?: CollaborationCampaignTriggerConditions | null;
  prompt_id?: string;
  variant_group_id?: string;
  variant_label?: string;
  is_control?: boolean;
  variant_split_percentage?: number;
  variant_metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CollaborationDispatch {
  id: string;
  campaign_id: string;
  brand_id: string;
  user_id: string;
  status: 'pending' | 'sending' | 'completed' | 'failed';
  total_recipients: number;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

export interface CollaborationSend {
  id: string;
  dispatch_id: string;
  campaign_email_id: string;
  collaborator_id: string;
  recipient_email: string;
  recipient_data?: Record<string, any>;
  status: 'scheduled' | 'sent' | 'failed' | 'replied' | 'unsubscribed';
  scheduled_at: string;
  sent_at?: string;
  opened_at?: string;
  replied_at?: string;
  error_message?: string;
  esp_message_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CollaborationPrompt {
  id: string;
  brand_id: string;
  prompt_type: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}

export interface CollaborationAutoCreateConfig {
  id: string;
  brand_id: string;
  metric_type: string;
  enabled: boolean;
  default_collaboration_type_id?: string;
  created_at: string;
  updated_at: string;
}
